import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Wallet, ArrowDownCircle, ArrowUpCircle, RefreshCw, ShieldAlert } from "lucide-react";

const WALLETS = [
  { key: "income", label: "Income wallet" },
  { key: "retail", label: "Retail wallet" },
  { key: "reward", label: "Reward wallet" },
  { key: "matching", label: "Matching wallet" },
  { key: "bonus", label: "Bonus wallet" },
  { key: "startup", label: "Startup wallet" },
];

/** Admin-only manual credit/debit of any IBA wallet with a mandatory reason.
 *  Every adjustment is stored in wallet_ledger + audit_log.
 */
export default function WalletAdjustCard({ userCode, currentWallets, onDone }) {
  const [wallet, setWallet] = useState("income");
  const [direction, setDirection] = useState("credit");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Amount must be > 0"); return; }
    if (reason.trim().length < 10) { toast.error("Reason must be at least 10 characters"); return; }
    if (!window.confirm(`${direction === "credit" ? "Credit" : "Debit"} ₹${amt.toFixed(2)} to ${wallet} wallet of ${userCode}?\nReason: ${reason.trim()}\nThis will be permanently audit-logged.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/wallet/adjust", {
        user_code: userCode, wallet, direction, amount: amt, reason: reason.trim(),
      });
      toast.success(`Wallet ${direction}ed — new ${wallet} balance ₹${(data.wallets?.[wallet] ?? 0).toLocaleString("en-IN")}`);
      setAmount(""); setReason("");
      onDone?.(data.wallets);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Adjustment failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="wallet-adjust-card" className="bg-white border border-border rounded-sm overflow-hidden">
      <div className="px-5 py-3 border-b border-border flex items-center gap-2">
        <Wallet size={14} className="text-gold-deep" />
        <h3 className="font-serif text-base text-navy">Manual wallet adjustment</h3>
      </div>
      <div className="p-5 space-y-3 text-sm">
        {/* Current balances snapshot */}
        <div className="flex flex-wrap gap-1.5">
          {WALLETS.map((w) => {
            const bal = currentWallets?.[w.key] ?? 0;
            return (
              <span key={w.key} data-testid={`wallet-bal-${w.key}`}
                className="text-[10px] uppercase tracking-widest px-2 py-1 border border-line rounded-sm text-navy/70 bg-cream/40">
                {w.key}: <b className="text-navy">₹{Number(bal).toLocaleString("en-IN")}</b>
              </span>
            );
          })}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-navy/50">Wallet</span>
            <select value={wallet} onChange={(e) => setWallet(e.target.value)}
              data-testid="wallet-adjust-wallet"
              className="w-full mt-1 border border-line rounded-sm px-2 py-1.5 text-sm focus:border-royal outline-none">
              {WALLETS.map((w) => <option key={w.key} value={w.key}>{w.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-navy/50">Direction</span>
            <div className="grid grid-cols-2 gap-1 mt-1">
              {[
                { k: "credit", l: "Credit", Icon: ArrowUpCircle, cls: "text-olive border-olive/40" },
                { k: "debit",  l: "Debit",  Icon: ArrowDownCircle, cls: "text-rose border-rose/40" },
              ].map(({ k, l, Icon, cls }) => (
                <button key={k} type="button" onClick={() => setDirection(k)}
                  data-testid={`wallet-adjust-dir-${k}`}
                  className={`inline-flex items-center justify-center gap-1 text-[11px] uppercase tracking-widest px-2 py-1.5 rounded-sm border ${direction === k ? `${cls} bg-white` : "border-line text-navy/50 bg-cream/30"}`}>
                  <Icon size={12} /> {l}
                </button>
              ))}
            </div>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-navy/50">Amount ₹</span>
            <input type="number" min="0.01" step="0.01" value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="wallet-adjust-amount"
              placeholder="0.00"
              className="w-full mt-1 border border-line rounded-sm px-2 py-1.5 text-sm focus:border-royal outline-none" />
          </label>
        </div>
        <label className="block">
          <span className="text-[10px] uppercase tracking-widest text-navy/50">Reason (min 10 chars, permanently audit-logged)</span>
          <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
            data-testid="wallet-adjust-reason"
            placeholder="e.g. Refund for order SO-12345 processed via UPI"
            className="w-full mt-1 border border-line rounded-sm px-3 py-2 text-sm focus:border-royal outline-none" />
        </label>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-[10.5px] text-navy/50">
            <ShieldAlert size={11} className="text-gold-deep" />
            Every adjustment is written to wallet_ledger + audit_log.
          </div>
          <button type="button" onClick={submit} disabled={busy}
            data-testid="wallet-adjust-submit"
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest px-4 py-2 bg-royal text-white hover:bg-royal-deep rounded-sm disabled:opacity-50">
            {busy ? <RefreshCw size={12} className="animate-spin" /> : (direction === "credit" ? <ArrowUpCircle size={12} /> : <ArrowDownCircle size={12} />)}
            {busy ? "Working…" : `${direction === "credit" ? "Credit" : "Debit"} ${amount ? "₹" + amount : ""}`}
          </button>
        </div>
      </div>
    </section>
  );
}
